import { useEffect, useState } from "react";

import { Menu, Sidebar, Spinner, TeamsLayoutContainer } from "@shapr3d/frontend-lib";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { TeamsHeader } from "../../components/Headers/TeamsHeader/TeamsHeader";
import { TeamSelectorActivator } from "../../components/TeamSelectorActivator/TeamSelectorActivator";
import { accountURL } from "../../config";
import useAccount from "../../hooks/useAccount";
import { useTeam } from "../../hooks/useTeams";
import useTracker from "../../utils/useTracker";

import style from "./TeamsLayout.module.css";

const goToAccount = (teamID: string) => {
  const url = new URL(accountURL);
  url.pathname = `/teams/${teamID}/members`;
  url.search = window.location.search;

  window.location.assign(url.toString());
};

const useGuardSyncOffTeams = () => {
  const { teamID } = useParams();
  const team = useTeam(teamID);

  useEffect(() => {
    if (team?.addons?.privateCloud || team?.addons?.syncForbidden) {
      goToAccount(team.id);
    }
  }, [team]);

  return team?.addons?.privateCloud || team?.addons?.syncForbidden;
};

export const TeamsLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { teamID } = useParams();
  const navigate = useNavigate();
  const tracker = useTracker();

  const { data: accountData } = useAccount();

  const currentTeam = useTeam(teamID);
  useEffect(() => {
    if (accountData) {
      if (!currentTeam) {
        const teamIDToNavigateTo =
          accountData.teams.find(({ isPrimary }) => isPrimary)?.id ?? accountData.teams[0].id;
        navigate(
          { pathname: `/teams/${teamIDToNavigateTo}/review-links`, search: location.search },
          { replace: true }
        );
      }
    }
  }, [accountData, teamID, navigate, location, currentTeam]);

  useEffect(() => {
    if (location.pathname === `/teams/${teamID}`) {
      navigate(
        { pathname: `/teams/${teamID}/review-links`, search: location.search },
        { replace: true }
      );
    }
  }, [location, teamID, navigate]);

  const isRedirecting = useGuardSyncOffTeams();

  if (!teamID || isRedirecting || !accountData) {
    return (
      <>
        <TeamsHeader />
        <div className={style.pageContainer}>
          <Spinner />
        </div>
      </>
    );
  }

  return (
    <>
      <TeamsHeader sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} teamId={teamID} />
      <TeamsLayoutContainer sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
        <Sidebar className={style.sidebar}>
          {accountData?.teams && (
            <>
              <Sidebar.Header>Teams</Sidebar.Header>
              <Sidebar.SpacerItem variant="bottom">
                <Menu
                  matchActivatorWidth
                  activator={<TeamSelectorActivator />}
                  onOpen={() => {
                    tracker.trackDashboardTeamSwitcherClicked({
                      activeTeamId: teamID,
                    });
                  }}
                >
                  {accountData?.teams.map((team) => (
                    <Menu.Item
                      key={team.id}
                      closeOnClick
                      onClick={() => {
                        tracker.trackDashboardTeamSwitcherItemClicked({
                          activeTeamId: teamID,
                          selectedTeamId: team.id,
                        });
                        navigate({ pathname: `/teams/${team.id}/review-links` });
                      }}
                    >
                      {team.name}
                    </Menu.Item>
                  ))}
                </Menu>
              </Sidebar.SpacerItem>
              <Sidebar.ScrollContainer>
                {teamID && (
                  <Sidebar.Item
                    selected={true}
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                  >
                    Review links
                  </Sidebar.Item>
                )}
              </Sidebar.ScrollContainer>
            </>
          )}
          <Sidebar.Divider />
          <Sidebar.Item
            onClick={() => {
              tracker.trackGoToMembers({
                activeTeamId: teamID,
                location: "web_dashboard_sidebar",
              });
              goToAccount(teamID);
            }}
          >
            Settings
          </Sidebar.Item>
        </Sidebar>
      </TeamsLayoutContainer>
    </>
  );
};
